import { LeaveRequestStatus } from '../models/attendance';

/**
 * Single source of truth for humanising a leave request status.
 *
 * The API returns the raw C# enum name ('Pending', 'CancellationPending', ...).
 * Screens used to render it with a bare `t(status)`, which only resolved for the
 * statuses that happen to have a flat key ('Pending'/'Approved'/'Rejected');
 * 'CancellationPending' had none, so the raw enum leaked to users in en, ar AND
 * fr alike, and 'Cancelled' only resolved by accident via the shared
 * common.json (where fr carries the plural "Annulées"). The fully-translated,
 * exhaustive set already lives under `employeeLeaves.status.*` in leaves.json
 * (all five statuses, all three locales) — this helper routes every render site
 * to it so the mapping is exhaustive by construction.
 *
 * Callers must have 'leaves' among their useTranslation namespaces (every
 * current leave screen does); the explicit `leaves:` prefix makes that
 * independent of namespace ordering.
 */

/** Structural subset of i18next's TFunction — avoids coupling to its generics. */
type Translate = (key: string, defaultValue?: string) => string;

const KNOWN_STATUSES = new Set<string>(Object.values(LeaveRequestStatus));

export function leaveStatusLabel(t: Translate, status?: string | null): string {
    if (!status) return '';
    // An unmapped value (a future server enum) degrades to the previous
    // behaviour rather than rendering a bare i18n key path.
    if (!KNOWN_STATUSES.has(status)) return t(status, status);
    return t(`leaves:employeeLeaves.status.${status}`, status);
}
